import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { AppView } from 'design-system';
import AmountPill from 'design-system/AmountPill';
import Button from 'design-system/Button';
import MoneyInput from 'design-system/MoneyInput';
import Spacer from 'design-system/Spacer';
import StatusBar from 'design-system/StatusBar';
import { rem } from 'design-system/values';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { editAutoInvestPlan } from '../actions/autoinvest';
import { selectActiveTradingAccountId, selectAutoinvestMinAmount } from '../reducers/selectors';
import { MAXIMUM_INVEST_AMOUNT, MINIMUM_AUTOINVEST_AMOUNT, MONTHLY_INVESTMENT_AMOUNTS } from '../constants';
const AutoinvestChooseAmountScreen = ({ route, navigation }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const moneyInputRef = useRef(null);
    const { initialAmount, planId } = route.params;
    const initialCollectionsFromRoute = route.params?.initialAllocations;
    const [, , loadingAction, fetch] = useFetchOld(muteErrorAndResult);
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const positionsCache = useAppSelector((state) => state.invest.tradingAccountPositionsCache);
    const accountPositions = tradingAccountId ? positionsCache?.[tradingAccountId] : undefined;
    const minAutoinvestAmount = useAppSelector(selectAutoinvestMinAmount);
    const initial = initialAmount ?? MINIMUM_AUTOINVEST_AMOUNT;
    const amountRef = useRef(initial);
    const [disabled, setDisabled] = useState(!initialAmount || initialAmount < minAutoinvestAmount);
    const isEditMode = initialAmount !== undefined;
    useLayoutEffect(() => {
        navigation.setOptions({
            title: 'Choose amount',
            headerTintColor: colors.text.primary,
            headerRight: () => <HelpScreenButton />,
        });
    }, [colors.text.primary]);
    const moneyInputColors = useMemo(() => ({
        input: colors.text.primary,
        pinpad: colors.text.primary,
        hintColor: colors.text.statusNegative,
    }), [colors.text.primary, colors.text.statusNegative]);
    const onAmountChange = useCallback((value) => {
        amountRef.current = value;
        setDisabled(value < minAutoinvestAmount || (isEditMode && value === initialAmount));
    }, [minAutoinvestAmount, isEditMode, initialAmount]);
    const handleContinue = useCallback(async () => {
        if (amountRef.current < minAutoinvestAmount) {
            moneyInputRef.current?.shake();
            return;
        }
        if (isEditMode && planId) {
            const result = await fetch(editAutoInvestPlan(planId, { amount: amountRef.current }));
            if (result && !('error' in result)) {
                navigation.goBack();
            }
            return;
        }
        const positionAssetIds = accountPositions ? Object.keys(accountPositions).filter((id) => accountPositions[id]) : [];
        const initialAllocations = positionAssetIds.length > 0
            ? (() => {
                const count = positionAssetIds.length;
                const base = Math.floor(100 / count / 5) * 5;
                const remainder = 100 - base * count;
                return positionAssetIds.map((assetId, i) => ({
                    assetId,
                    percentage: base + (i === 0 ? remainder : 0),
                }));
            })()
            : undefined;
        navigation.navigate('AutoinvestAllocation', {
            accountType: route.params.accountType,
            collectionDate: route.params.collectionDate,
            amount: amountRef.current,
            initialAllocations: initialCollectionsFromRoute || initialAllocations,
        });
    }, [
        minAutoinvestAmount,
        isEditMode,
        planId,
        accountPositions,
        route.params.accountType,
        route.params.collectionDate,
        initialCollectionsFromRoute,
    ]);
    const setAmount = useCallback((amount) => () => {
        moneyInputRef.current?.setAmount(amount);
    }, []);
    const numPadHeader = useMemo(() => (<View style={styles.numPadHeader}>
        <Spacer h={24}/>
        <View style={styles.amountSelectors}>
          {MONTHLY_INVESTMENT_AMOUNTS.map((amount) => (<AmountPill selected key={amount} value={amount} currency="GBP" onPress={setAmount(amount)} style={styles.amountPill}/>))}
        </View>
      </View>), [setAmount, styles.amountPill, styles.amountSelectors, styles.numPadHeader]);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, styles.container);
    return (<AppView type="primary" avoidHeader>
      <View style={paddingBottom}>
        <StatusBar barStyle="dark-content" animated/>
        <MoneyInput flex showMinHint showMaxHint ref={moneyInputRef} initialValue={initial} colors={moneyInputColors} min={minAutoinvestAmount} max={MAXIMUM_INVEST_AMOUNT} pinPadStyle={styles.pinPad} numPadHeader={numPadHeader} onValueChange={onAmountChange} alwaysDisplayMinHint={false} alwaysDisplayMaxHint={false}/>
        <Spacer h={30}/>
        <View style={styles.buttonContainer}>
          <Button brand title={isEditMode ? 'Save' : 'Continue'} onPress={handleContinue} disabled={disabled} loading={loadingAction}/>
        </View>
      </View>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    buttonContainer: {
        paddingHorizontal: rem(16),
    },
    numPadHeader: {
        flex: 2,
        width: '100%',
    },
    amountSelectors: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'center',
    },
    amountPill: {
        marginHorizontal: rem(4),
    },
    pinPad: {
        marginTop: rem(10),
    },
}));
export default AutoinvestChooseAmountScreen;
